import React, { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import { Column } from 'components/UI/Column';
import { Row } from 'components/UI/Row';
import { useDownloadPrintFile } from 'features/SerialNumbers/useSerialDownloads';
import { ProductProperty } from 'features/Products/productsTypes';
import { getSelectedSerialNumbers } from 'features/SerialNumbers/serialNumbers';

// Import label previews.
import noPreview from '../../resources/no-preview.png';
import l2x1 from '../../resources/2x1.png';
import l2d75x1d25 from '../../resources/2.75x1.25.png';
import l2x1at203dpi from '../../resources/2x1-203dpi.png';
import l2d75x1d25at203dpi from '../../resources/2.75x1.25-203dpi.png';
import l4x2at203dpi from '../../resources/2.75x1.25-203dpi.png';
import l4x6at203dpi from '../../resources/4x6-203dpi.png';

const previews: any = {
  '-': noPreview,
  '2w1h': l2x1,
  '2.75w1.25h': l2d75x1d25,
  '2w1h-203dpi': l2x1at203dpi,
  '2.75w1.25h-203dpi': l2d75x1d25at203dpi,
  '4w2h-203dpi': l4x2at203dpi,
  '4w6h-203dpi': l4x6at203dpi,
};

const formats: Array<string> = ['pdf', 'txt-zpl', 'zpl'];
const propertiesSupport: Map<string, boolean> = new Map([
  ['-', false],
  ['2w1h', true],
  ['2w1h-203dpi', true],

  ['2.75w1.25h', true],
  ['2.75w1.25h-203dpi', true],
  ['4w2h-203dpi', true],
  ['4w6h-203dpi', false]
]);
const footerSupport: Map<string, boolean> = new Map([
  ['-', false],
  ['2w1h', false],
  ['2w1h-203dpi', false],

  ['2.75w1.25h', true],
  ['2.75w1.25h-203dpi', true],
  ['4w2h-203dpi', true],
  ['4w6h-203dpi', false]
]);
const sizes: Map<string, Array<Array<string>>> = new Map([
  [
    'zpl',
    [
      ['-', 'print.select size'],
      ['2w1h-203dpi', 'print.sizes.2w1h-203dpi'],
      ['2.75w1.25h-203dpi', 'print.sizes.2d75w1d25h-203dpi'],
      ['4w2h-203dpi', 'print.sizes.4w2h-203dpi'],
      ['4w6h-203dpi', 'print.sizes.4w6h-203dpi']
    ]
  ],
  [
    'txt-zpl',
    [
      ['-', 'print.select size'],
      ['2w1h-203dpi', 'print.sizes.2w1h-203dpi'],
      ['2.75w1.25h-203dpi', 'print.sizes.2d75w1d25h-203dpi'],
      ['4w2h-203dpi', 'print.sizes.4w2h-203dpi'],
      ['4w6h-203dpi', 'print.sizes.4w6h-203dpi']
    ]
  ],
  [
    'pdf',
    [
      ['-', 'print.select size'],
      ['2w1h', 'print.sizes.2w1h'],
      ['2.75w1.25h', 'print.sizes.2d75w1d25h']
    ]
  ]
]);

type ConfigureLabelForPrintProps = {
  closeFn: () => void;
  completeFn?: () => void;
  isOpen: boolean;
  properties: Array<ProductProperty>;
  targets: Array<string>;
};

const ConfigureLabelForPrint: FC<ConfigureLabelForPrintProps> = ({ closeFn, isOpen, properties, completeFn, targets }) => {
  const { t } = useTranslation();
  const [configuration, setConfiguration] = useState<any>({ size: '-', format: 'pdf', properties: [], shortProperties: false, footer: '' });
  const canPrint = configuration.size !== '-' && configuration.format !== '-';

  const { getFile } = useDownloadPrintFile(
    targets,
    configuration.format,
    configuration.size,
    configuration.properties,
    configuration.shortProperties,
    configuration.footer
  );

  const print = () => {
    getFile();
    closeFn();
    if (completeFn) {
      completeFn();
    }

    setConfiguration({ size: '-', format: 'pdf', properties: [], shortProperties: false, footer: '' });
  };

  const sizeSupportsProperties = properties.length > 0 && (propertiesSupport.get(configuration.size) ?? false);
  const sizeSupportsFooter = footerSupport.get(configuration.size) ?? false;
  const selectedPropertiesCount = (configuration.properties ?? []).length;
  const propertiesSelectorLabel =
    selectedPropertiesCount <= 0
      ? t('products.property.print product name')
      : t('products.property.print', { count: selectedPropertiesCount });

  return (
    <Dialog maxWidth="lg" open={isOpen} onClose={closeFn} aria-labelledby="configure-label-print-title">
      <DialogTitle id="component-form-title">{t('print.select size and format')}</DialogTitle>

      <DialogContent>
        <Column>
          <Row>
            <TextField
              fullWidth
              variant="outlined"
              label={t('print.size')}
              select
              value={configuration.size}
              style={{ marginBottom: '16px' }}
              onChange={e => {
                setConfiguration({ ...configuration, size: e.target.value });
              }}
            >
              {sizes.get(configuration.format)!.map((value, key) => (
                <MenuItem key={key} value={value[0]}>
                  {t(value[1])}
                </MenuItem>
              ))}
            </TextField>
          </Row>

          <Row>
            <TextField
              fullWidth
              variant="outlined"
              label={t('print.format')}
              select
              value={configuration.format}
              style={{ marginBottom: '16px' }}
              onChange={e => {
                setConfiguration({ ...configuration, size: '-', format: e.target.value });
              }}
            >
              {formats.map((value, key) => (
                <MenuItem key={key} value={value}>
                  {t(`print.formats.${value}`)}
                </MenuItem>
              ))}
            </TextField>
          </Row>

          {sizeSupportsProperties && (
            <>
            <Row>
              <FormControl variant="outlined" fullWidth>
                <InputLabel id="property-label">{propertiesSelectorLabel}</InputLabel>
                <Select
                  multiple
                  fullWidth
                  displayEmpty
                  variant="outlined"
                  labelId="property-label"
                  style={{ marginBottom: '16px' }}
                  value={configuration.properties}
                  label={propertiesSelectorLabel}
                  onChange={event => setConfiguration({ ...configuration, properties: event.target.value })}
                >
                  {properties.map((item, index) => (
                    <MenuItem key={index} value={index}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Row>

            <Row>
              <FormControlLabel label={t('products.property.shortened')}
                control={
                  <Checkbox
                    checked={configuration.shortProperties}
                    onChange={(e, value) => setConfiguration({ ...configuration, shortProperties: value})} />
                  }
              />
            </Row>
            </>
          )}

          {sizeSupportsFooter && (
            <Row>
              <FormControl variant="outlined" fullWidth>
                <TextField
                  variant="outlined"
                  label={t('products.property.footer')}
                  value={configuration.footer}
                  style={{ marginTop: '16px', marginBottom: '16px' }}
                  onChange={(event) => setConfiguration({ ...configuration, footer: event.target.value.toUpperCase()})}
                />
              </FormControl>
            </Row>
          )}

          <Column>
            <Row>
              <Typography>
                {configuration.size === '-' ? t('label.preview not available') : t('label.preview')}
              </Typography>
            </Row>
            <Row alignItems="center" style={{ display: 'flex', paddingTop: 8 }}>
              <div style={{ flexGrow: 1 }} />
              <img src={previews[configuration.size]} style={{ width: 'auto', height: 200 }} />
              <div style={{ flexGrow: 1 }} />
            </Row>
          </Column>
        </Column>
      </DialogContent>

      <DialogActions>
        <Button color="secondary" onClick={closeFn}>
          {t('label.cancel')}
        </Button>

        <Button color="primary" onClick={print} disabled={!canPrint}>
          {t('label.print')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { ConfigureLabelForPrint };
